import axios from "axios";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import {
  getAccessToken,
  setAccessToken,
  clearTokens,
  getIsAdminView,
} from "../lib/tokenService";
import { clearPortalAuthSnapshot } from "./authStorage";
import { getApiFailureKind, isRetryableApiError } from "./apiError";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

export const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
  // 🚨 Add this globally: Necessary for every request that might need the cookie
  withCredentials: true,
});

// --- STATE MANAGEMENT FOR REFRESH LOGIC (RETAINS) ---
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

type ReliabilityMeta = {
  captureErrors?: "always" | "never";
  retryPolicy?: "login" | "refresh" | "none";
  retryCount?: number;
  skipAuth?: boolean;
  onRetry?: () => void;
};

type ReliabilityConfig = {
  method?: string;
  url?: string;
  meta?: ReliabilityMeta;
  headers: Record<string, string>;
};

const getRetryLimit = (config?: ReliabilityConfig) => {
  if (config?.meta?.retryPolicy === "none") return 0;
  if (config?.meta?.retryPolicy === "login" || config?.meta?.retryPolicy === "refresh") return 1;
  return config?.method?.toLowerCase() === "get" ? 2 : 0;
};

const getRetryDelay = (retryCount: number) => {
  const ceiling = retryCount === 0 ? 700 : 2_000;
  return 250 + Math.floor(Math.random() * (ceiling - 250 + 1));
};

const wait = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

const captureTerminalFailure = (error: unknown, config?: ReliabilityConfig) => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  const kind = getApiFailureKind(error);
  const captureOverride = config?.meta?.captureErrors;
  const shouldCapture =
    captureOverride === "always" ||
    (captureOverride !== "never" &&
      kind !== "cancelled" &&
      (kind === "transport" || kind === "timeout" || kind === "server"));

  if (!shouldCapture) return;

  Sentry.withScope((scope) => {
    scope.setTags({
      "api.failure_kind": kind,
      "api.method": config?.method?.toUpperCase() ?? "UNKNOWN",
      "api.route": config?.url?.split("?")[0] ?? "unknown",
      "api.attempts": String((config?.meta?.retryCount ?? 0) + 1),
      ...(status ? { "api.status": String(status) } : {}),
    });
    scope.setFingerprint([
      "api-terminal-failure",
      config?.method?.toUpperCase() ?? "UNKNOWN",
      config?.url?.split("?")[0] ?? "unknown",
      kind,
      String(status ?? "no-response"),
    ]);
    Sentry.captureException(error);
  });
};

const shouldSkipRefresh = (url?: string) => {
  if (!url) {
    return false;
  }

  return [
    "/auth/staff/login",
    "/auth/login",
    "/auth/refresh-token",
    "/auth/send-otp",
    "/auth/verify-otp",
    "/auth/verify-forgot-password-otp",
    "/auth/update-password",
    "/auth/check-email",
    "/auth/logout",
    "/auth/admin-view/exchange",
  ].some((path) => url.includes(path));
};

const forceReauth = () => {
  clearPortalAuthSnapshot();
  clearTokens();
  window.location.replace("/login");
};

// Add this before your response interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken(); // Get your token from storage
    const meta = (config as ReliabilityConfig).meta;
    if (token && !meta?.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as ReliabilityConfig | undefined;
    const status = error.response?.status;
    const responseData = error.response?.data;
    const errorMessage =
      responseData?.error || responseData?.message || "Request failed";

    if (
      status === 403 &&
      errorMessage === "This action is disabled in read-only admin view."
    ) {
      toast.error("This action is disabled in read-only admin view.");
      return Promise.reject(error);
    }

    if (
      status === 403 &&
      errorMessage === "Invalid token" &&
      !shouldSkipRefresh(originalRequest?.url)
    ) {
      forceReauth();
      return Promise.reject(error);
    }

    if (
      status === 401 &&
      getIsAdminView() &&
      !shouldSkipRefresh(originalRequest?.url)
    ) {
      toast.error("Admin view session expired.");
      forceReauth();
      return Promise.reject(error);
    }

    if (
      status === 401 &&
      !originalRequest._retry &&
      !shouldSkipRefresh(originalRequest?.url)
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (newAccessToken) => {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              resolve(axiosInstance(originalRequest));
            },
            reject,
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshResponse = await axiosInstance.post(
          "/auth/refresh-token",
          {},
          {
            meta: { retryPolicy: "refresh", skipAuth: true },
          } as never,
        );

        const newAccessToken = refreshResponse.data.accessToken;
        setAccessToken(newAccessToken);

        failedQueue.forEach(({ resolve }) => resolve(newAccessToken));
        failedQueue = [];

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        failedQueue.forEach(({ reject }) => reject(refreshError));
        failedQueue = [];
        const refreshStatus = axios.isAxiosError(refreshError)
          ? refreshError.response?.status
          : undefined;
        // A transport failure does not prove the session is invalid. Keep the
        // local session intact and let the UI recover when connectivity returns.
        if (refreshStatus === 401 || refreshStatus === 403) {
          forceReauth();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    const retryCount = originalRequest?.meta?.retryCount ?? 0;
    const retryLimit = getRetryLimit(originalRequest);
    if (originalRequest && isRetryableApiError(error) && retryCount < retryLimit) {
      Sentry.addBreadcrumb({
        category: "api.retry",
        level: "warning",
        message: "Retrying recoverable API request",
        data: {
          method: originalRequest.method?.toUpperCase(),
          route: originalRequest.url?.split("?")[0],
          attempt: retryCount + 1,
          failureKind: getApiFailureKind(error),
        },
      });
      originalRequest.meta?.onRetry?.();
      originalRequest.meta = { ...originalRequest.meta, retryCount: retryCount + 1 };
      await wait(getRetryDelay(retryCount));
      return axiosInstance(originalRequest);
    }

    captureTerminalFailure(error, originalRequest);

    return Promise.reject(error);
  }
);
export default axiosInstance;
